import { CommonModule, Location } from '@angular/common';
import { BrisbaneDatePipe } from '../../shared/pipes/brisbane-date.pipe';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, forkJoin, take, tap } from 'rxjs';
import { userRoles } from '../../../environments/environment';
import { ButtonComponent } from '../../shared/components/custom-button/button.component';
import { TableComponent } from '../../shared/components/custom-table/table.component';
import { AddBulkDialogComponent } from '../../shared/components/dialog/add-bulk-upload/addBulk-dialog.component';
import { AddStudentComponent } from '../../shared/components/dialog/add-student/add-student.component';
import { AddTeacherStudentComponent } from '../../shared/components/dialog/add-teacher-student/add-teacher-student.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { ExportDropdownComponent } from '../../shared/components/export-dropdown/export-dropdown';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../shared/services/http.service';
import { UrlParserService } from '../../shared/services/url-parser.service';

interface TeacherDialogData {
  initialData?: APIResponse;
  EditMode: boolean;
}

interface APIResponse {
  comp: any;
  past: any;
  yearLevelId: string;
  ethnicityId: string;
  id?: string | number;
  schoolId?: string | number;
  name?: string;
  dateOfBirth?: string;
  schoolName?: string;
  studentId?: string;
  firstName?: string;
  lastName?: string;
  schoolEmail?: string;
  mobileNumber?: string;
  personalEmail?: string;
  gender?: string;
  postcode?: string;
  teacherName?: string;
  ethnicity?: string;
  dateRegistered?: string;
  lastLogin?: string;
  registrationType?: string;
  studentStatus?: string;
  yearLevel?: number;
  status?: string;
  userId?: string;
  region?: string;
  email?: string;
  dateRegistration?: string;
  pdPoints?: number;
  age?: string;
  parentGuardianEmail?: string;
  parentGuardianFullName?: string;
  totalPoints?: string;
  eventAttendancePoints?: string;
  workExperiencePoints?: string;
  assignmentCompleted?: string;
  isMediaConsentParentApprovalRequired?: string;
  parentApprovalStatus?: string;
  lastUpdated?: string;
  parentEmail?: string;
  parentName?: string;
  assignedTeacherName?: string;
  role?: string;
  teacherId?: string;
  eventID?: string;
  eventName?: string;
  eventStartDate?: string;
  location?: string;
  eventManager?: string;
  notes?: string;
  isTeacherVerified?: boolean;
  teacherVerifiedAt?: string;
  teacherVerifiedByName?: string;
}

interface listAPIResponse {
  status: boolean;
  message: string;
  data: {
    lists: any[];
    totalRecord: number;
    items: any[];
  };
}

interface UsersPagedResponse {
  parentGuardianEmail?: string;
  parentGuardianFullName?: string;
  items?: APIResponse[];
  totalRecords?: number;
  totalRecord?: number;
  lists?: APIResponse[];
}
interface ApiDataResponse {
  status: boolean;
  message: string;
  data: any;
}

interface TeacherAPIResponse {
  schoolId: string;
  schoolName: string;
  address: string;
  suburb: string;
  postcode: string;
  teacherId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  isActive: boolean | string;
  dateOfBirth: string;
  assignedTeacher: string;
  name: string;
  id: string;
}

interface TeacherApiDataResponse {
  status: boolean;
  message: string;
  data: TeacherAPIResponse | TeacherAPIResponse[];
}

function isPagedResponse(
  data: UsersPagedResponse | APIResponse[],
): data is UsersPagedResponse {
  return !!(data as UsersPagedResponse)?.items;
}

@Component({
  selector: 'app-students',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ButtonComponent,
    MatDialogModule,
    ReactiveFormsModule,
    FormsModule,
    ExportDropdownComponent,
    BrisbaneDatePipe,
  ],
  templateUrl: './students.component.html',
  styleUrls: ['./students.component.scss'],
})
export class StudentsComponent implements OnInit, AfterViewInit {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild('statusButton') showstatus!: TemplateRef<any>;
  @ViewChild('actionButtonsStudent') actionButtonsStudent!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;
  @ViewChild('addStudentContent') addStudentContent!: TemplateRef<any>;
  @ViewChild('addSchoolContent') addSchoolContent!: TemplateRef<any>;
  @ViewChild('addSchoolContent2') addSchoolContent2!: TemplateRef<any>;
  @ViewChild('addTeacherContent') addTeacherContent!: TemplateRef<any>;
  @ViewChild('tableContainer') tableContainer!: ElementRef;

  @ViewChild('schoolDetailsDialog') schoolDetailsDialog!: TemplateRef<any>;
  @ViewChild('teacherActivitiesActionTpl') teacherActivitiesActionTpl!: TemplateRef<any>;
  selectedSegment:
    | 'recentlyRegisteredEvents'
    | 'eventattended'
    | 'associatedTeachers'
    | 'notes' = 'associatedTeachers';
  form!: FormGroup;
  teacherForm!: FormGroup;
  userForm!: FormGroup;
  isProcessing = false;
  editingStudentIndex: number | null = null;
  editingSchool: any = null;
  viewMode = false;
  selectedStudentId: string | null = null;
  selectedStudent: any | null = null;
  pointsStudent: any | null = null;
  loading = false;
  tabelLoading: boolean = false;
  schoolDialogLoading = false;
  schoolDetailDialogRef: any = null;
  isDownloadingAccount = false;
  isDownloadingMedia = false;
  isResendingParentConsent = false;
  isResettingParentConsent = false;
  isVerifyingStudent = false;
  resendCooldownActive = false;
  resendCooldownSeconds = 0;
  showResendCooldownError = false;
  private resendCooldownInterval: any = null;
  accountApprovedStatus: string = 'Approved';
  mediaConsentApprovedStatus: string = 'Rejected';

  columns: string[] = [
    'student name',
    'email',
    'school name',
    'age',
    'PD points',
    'last login / last updated',
    'Date registered',
    'Registration type',
    'parent consent',
    'status',
  ];
  exportColumns: string[] = [
    'Student name',
    'Email',
    'School name',
    'Age',
    'PD points',
    'Last login / Last updated',
    'Date registered',
    'Registration type',
    'Parent consent',
    'Status',
  ];
  recentEventColumns: string[] = ['event name', 'date', 'status'];
  attendedEventColumns: string[] = ['event name', 'date', 'status'];
  teacherColumn: string[] = ['name', 'email', 'role'];
  studentSortableColumns: string[] = [
    'student name',
    'email',
    'school name',
    'age',
    'PD points',
    'last login / last updated',
    'Date registered',
    'Registration type',
    'parent consent',
    'student status',
    'status',
  ];
  studentClickableColumns: string[] = ['student name'];
  columnsTeacher: string[] = [
    'student name',
    'email',
    'school name',
    'age',
    'PD points',
    'last login / last updated',
    'Date registered',
    'Registration type',
    'parent consent',
    'student status',
    'status',
  ];
  columnsCSQUser: string[] = [
    'student name',
    'email',
    'school name',
    'age',
    'PD points',
    'last login / last updated',
    'Date registered',
    'Registration type',
    'parent consent',
    'student status',
    'status',
  ];

  actionTemplateReady = false;
  studentOption: any[] = [];
  teachersOption: any[] = [];
  genderOption: any[] = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Non-identifying', value: 'non-identifying' },
    { label: 'Prefer not to say', value: 'prefernottosay' },
  ];
  schoolOptions: any[] = [];
  teacherMode: boolean = false;
  parentConsent: boolean = false;
  downloadInProgress: boolean = false;
  downloadingRowId: string | null = null;
  exportUrl: string = '';
  private currentRequestId = 0;
  private isPageChangeLoading = false;

  customErrorMessages = {
    firstName: { required: 'Please enter a first name.' },
    lastName: { required: 'Please enter a last name.' },
    personalEmail: {
      required: 'Enter a valid personal email.',
      email: 'Please enter a valid email',
    },
    schoolEmail: {
      required: 'Enter a valid school email.',
      email: 'Please enter a valid email',
    },
    dob: {
      required: 'Enter a valid date of birth.',
      pattern: 'Please enter a valid date',
    },
    postcode: {
      required: 'Please enter a valid Postcode.',
      pattern: 'Please enter numeric value.',
    },
    mobileNumber: {
      required: 'Please enter a phone number.',
      pattern: 'Please enter a valid phone number.',
    },
    schoolName: { required: 'Please select a school.' },
    gender: { required: 'Please select a gender.' },
    assignedTeacher: { required: 'Please assign a student.' },
    ethnicity: { required: 'Ethnicity is required.' },
  };

  teacherCustomErrorMessages = {
    firstName: { required: 'Please enter a first name.' },
    lastName: { required: 'Please enter a last name.' },
    email: {
      required: 'Please enter a valid email address.',
      email: 'Please enter a valid email',
    },
    phoneNumber: {
      required: 'Please enter a valid mobile number.',
      pattern: 'Please enter numeric value.',
    },
    school: { required: 'Please select a school.' },
  };

  teacherSchoolOptions: any[] = [];
  fullData: any[] = [];
  data: any[] = [];
  search: string = '';
  searchControl = new FormControl('');

  upcomingEventsData: any = [];
  eventAttendedsData: any = [];
  associatedTeachersData: any = [];
  teacherActivitiesData: any[] = [];
  teacherActivitiesLoading = false;
  teacherActivitiesColumns: string[] = ['event name', 'source', 'Date & time', 'location', 'status'];
  selectedSchool: any = {};
  schoolTeacherData: any[] = [];
  schoolContactData: any[] = [];
  schoolTeacherColumns: string[] = ['teacher name', 'email', 'phone/mobile'];
  csqUserMode: boolean = false;
  teacherLoading = false;
  schoolId: string = '';

  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | '' = '';

  userRoles = {
    addNew: false,
    export: false,
    bulkUpload: false,
    listItem: {
      edit: false,
      view: false,
      delete: false,
      statusChange: false,
    },
  };

  userViewRoles: { edit: boolean; schoolView: boolean; resetConsent?: boolean; changeMediaConsent?: boolean } = {
    edit: false,
    schoolView: false,
  };

  isUpdatingMediaConsent = false;

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private location: Location,
    private urlParser: UrlParserService,
  ) {
    this.initForms();
    const role = localStorage.getItem('userRole');
    this.teacherMode = role === 'teacher' ? true : false;
    this.csqUserMode = role === 'csquser' ? true : false;
    const UserDetailsStr = localStorage.getItem('userDetails') || '';

    try {
      const UserDetails = UserDetailsStr ? JSON.parse(UserDetailsStr) : null;
      if (UserDetails) {
        this.schoolId = UserDetails.schoolId;
      }
    } catch (e) {
      this.schoolId = '';
    }
  }

  ngOnInit(): void {
    setTimeout(() => this.resetTableScroll(), 0);

    this.route.paramMap.subscribe((params) => {
      const encodedId = params.get('id');
      const id = encodedId ? this.urlParser.decodeId(encodedId) : null;
      this.viewMode = !!id;
      this.selectedStudentId = id || null;
      if (this.viewMode && this.selectedStudentId) {
        this.fetchViewStudents(this.selectedStudentId);
        if (!this.teacherMode) {
          this.fetchAssociatedTeacher(this.selectedStudentId);
        } else {
          this.fetchStudentActivitiesForTeacher(this.selectedStudentId);
        }
      }
      setTimeout(() => this.resetTableScroll(), 100);
    });

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const page = params['page'] ? parseInt(params['page']) : 1;
      this.currentPage = page;

      if (!this.viewMode) {
        if (this.teacherMode) {
          this.fetchTeacherStudents(this.currentPage);
        } else {
          this.fetchStudents(this.currentPage);
        }
      }
    });

    this.form.get('schoolName')?.valueChanges.subscribe((schoolId) => {
      if (schoolId) {
        this.fetchTeachersDropDown(schoolId).subscribe();
      } else {
        this.teachersOption = [];
        this.form.patchValue({ assignedTeacher: null });
      }
    });

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles = userRoles[user as keyof typeof userRoles]?.student;
      this.userViewRoles =
        userRoles[user as keyof typeof userRoles]?.studentView;
    }

    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.search = searchValue || '';
        this.currentPage = 1;
        this.updateUrlWithPage(1);
        if (this.teacherMode) {
          this.fetchTeacherStudents(this.currentPage);
        } else {
          this.fetchStudents(this.currentPage);
        }
      });
  }

  private initForms(): void {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      personalEmail: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      schoolEmail: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      schoolName: ['', [Validators.required]],
      dob: ['', [Validators.required]],
      mobileNumber: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
      postcode: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      gender: ['', [Validators.required]],
      assignedTeacher: ['', [Validators.required]],
      ethnicity: ['', [Validators.required]],
    });

    this.teacherForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      phoneNumber: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
      school: ['', Validators.required],
    });

    this.userForm = this.fb.group({
      studentName: [''],
      email: [''],
      school: [''],
      region: [''],
      status: [''],
    });
  }

  private getSortColumnName(column: string): string {
    const columnMapping: { [key: string]: string } = {
      'student name': 'StudentName',
      email: 'Email',
      'school name': 'SchoolName',
      'date registered': 'DateRegistered',
      'pd points': 'PDPoints',
      'last login / last updated': 'LastLogin',
      'last login': 'LastLogin',
      'registration type': 'RegistrationType',
      'parent consent': 'ParentConsent',
      'status': 'Status',
      'age': 'Age',
      'student status': 'StudentStatus',
      studentname: 'StudentName',
      schoolname: 'SchoolName',
      dateregistered: 'DateRegistered',
      pdpoints: 'PDPoints',
      lastlogin: 'LastLogin',
    };

    return columnMapping[column.toLowerCase()] || column;
  }

  getExportApiUrl(): string {
    if (this.teacherMode) {
      if (this.search && this.search.trim() !== '') {
        return `teacherstudent/${this.schoolId}/students/search/exports`;
      } else {
        return `teacherstudent/${this.schoolId}/students/exports`;
      }
    } else {
      if (this.search && this.search.trim() !== '') {
        return 'students/get/search/exports';
      } else {
        return 'students/exports';
      }
    }
  }

  getCurrentExportParams(): any {
    const params: any = {
      orderby: this.sortDirection || 'desc',
      orderbyname: this.sortColumn
        ? this.getSortColumnName(this.sortColumn)
        : 'CreatedAt',
    };

    if (this.search && this.search.trim() !== '') {
      params.search = this.search;
    }

    return params;
  }

  onExportComplete(type: 'csv' | 'pdf' | 'excel'): void {
    this.showSuccessToast(
      `Export completed successfully as ${type.toUpperCase()}`,
    );
  }

  private handleApiError(error: any, defaultMessage: string): void {
    this.showErrorToast(error ?? defaultMessage);
    this.loading = false;
  }

  private mapStudentData(student: APIResponse): any {
    this.parentConsent =
      student.parentApprovalStatus === 'Approved' ? true : false;

    return {
      id: student.studentId ?? '',
      'student name': `${student.firstName ?? ''} ${student.lastName ?? ''}`,
      firstName: student.firstName ?? '',
      lastName: student.lastName ?? '',
      dateOfBirth: student.dateOfBirth
        ? new Date(student.dateOfBirth).toLocaleDateString('en-GB')
        : '',
      dob: student?.dateOfBirth,
      email: student.schoolEmail ?? '',
      'school name': student.schoolName ?? '',
      assignedTeacher: student.assignedTeacherName ?? '',
      personalEmail: student.personalEmail ?? '',
      schoolEmail: student.schoolEmail ?? '',
      gender: student.gender ?? '',
      ethnicity: student.ethnicity ?? '',
      schoolName: student.schoolName ?? '',
      teacherName: student.teacherName ?? '',
      mobileNumber: student.mobileNumber ?? '',
      postcode: student.postcode ?? '',
      'last login / last updated': this.formatStudentLastAccess(student),
      'Date registered': student.dateRegistered
        ? new Date(student.dateRegistered).toLocaleDateString('en-GB')
        : '',
      registrationType: student.registrationType ?? '',
      'Registration type': student.registrationType ?? '',
      yearLevel: student.yearLevel ?? null,
      userId: student.userId ?? null,
      schoolId: student?.schoolId,
      age: student?.age ? student?.age : '-',
      'parent/Guardian full name': student?.parentGuardianFullName
        ? student?.parentGuardianFullName
        : '-',
      'parent/Guardian email': student?.parentGuardianEmail
        ? student?.parentGuardianEmail
        : '-',
      parentName: student?.parentGuardianFullName
        ? student?.parentGuardianFullName
        : '-',
      parentEmail: student?.parentGuardianEmail
        ? student?.parentGuardianEmail
        : '-',
      parentApprovalStatus: student.parentApprovalStatus ?? '',
      pdPoints: student.pdPoints ?? 0,
      'PD points': student.pdPoints ?? 0,
      status: this.mapStudentListTableStatus(student.status),
      'student status': this.getStudentViewBadgeLabel(student) ?? '-',
      region: student?.region ?? '',
      'parent consent':
        (student.parentApprovalStatus === 'Approved' ||
          student.parentApprovalStatus === 'Declined' ||
          student.parentApprovalStatus === 'Rejected')
          ? 'Download'
          : 'Pending',
      isDownloading: false,
      isStatusClickable: true,
      isConsentClickable:
        student.parentApprovalStatus === 'Approved',
      ethnicityId: student?.ethnicityId ?? '',
      yearLevelId: student?.yearLevelId ?? '',
      past: student?.past,
      comp: student?.comp,
    };
  }

  private mapTeacherStudentData(student: APIResponse): any {
    this.parentConsent = !!student.parentApprovalStatus;
    return {
      id: student.studentId ?? '',
      'student name': `${student.firstName} ${student.lastName}`,
      dateOfBirth: student.dateOfBirth
        ? new Date(student.dateOfBirth).toLocaleDateString('en-GB')
        : '',
      firstName: student.firstName ?? '',
      lastName: student.lastName ?? '',
      ethnicityId: student?.ethnicityId ?? '',
      yearLevelId: student?.yearLevelId ?? '',
      email: student.schoolEmail ?? '',
      personalEmail: student.personalEmail ?? '',
      schoolEmail: student.schoolEmail ?? '',
      gender: student.gender ?? '',
      ethnicity: student.ethnicity ?? '',
      schoolName: student.schoolName ?? '',
      'Date registered': student.dateRegistration
        ? new Date(student.dateRegistration).toLocaleDateString('en-GB')
        : '',
      'last login / last updated': this.formatStudentLastAccess(student),
      'Registration type': student.registrationType ?? '',
      yearLevel: student.yearLevel ?? null,
      userId: student.userId ?? null,
      schoolId: student?.schoolId,
      'school name': student?.schoolName,
      userID: student?.userId,
      age: student?.age ? student?.age : '-',
      'parent/Guardian full name': student?.parentGuardianFullName
        ? student?.parentGuardianFullName
        : '-',
      'parent/Guardian email': student?.parentGuardianEmail
        ? student?.parentGuardianEmail
        : '-',
      parentName: student?.parentGuardianFullName
        ? student?.parentGuardianFullName
        : '-',
      parentEmail: student?.parentGuardianEmail
        ? student?.parentGuardianEmail
        : '-',
      parentApprovalStatus: student.parentApprovalStatus ?? '',
      pdPoints: student.pdPoints ?? 0,
      'PD points': student.pdPoints ?? 0,
      status: this.mapStudentListTableStatus(student.status),
      'student status': this.getStudentViewBadgeLabel(student) ?? '-',
      'parent consent':
        (student.parentApprovalStatus === 'Approved' ||
          student.parentApprovalStatus === 'Declined' ||
          student.parentApprovalStatus === 'Rejected')
          ? 'Download'
          : 'Pending',
      isDownloading: false,
      isStatusClickable: true,
      isConsentClickable:
        student.parentApprovalStatus === 'Approved',
      past: student?.past,
      comp: student?.comp,
    };
  }

  private mapStudentListTableStatus(
    status: string | null | undefined,
  ): string {
    const trimmedStatus = status?.trim();

    if (!trimmedStatus) {
      return 'Active';
    }

    return trimmedStatus.toLowerCase() === 'pending'
      ? 'Inactive'
      : trimmedStatus;
  }

  updateSearch(searchValue: string): void {
    this.search = searchValue;
    this.searchControl.setValue(searchValue);
    this.cdr.detectChanges();
  }

  fetchStudents(page: number = 1): void {
    this.loading = true;
    const requestId = ++this.currentRequestId;

    let url: string;
    if (this.search && this.search.trim() !== '') {
      url = `students/get/search?search=${encodeURIComponent(this.search)}&page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    } else {
      url = `students?page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    }

    this.httpService.get<listAPIResponse>(url).subscribe({
      next: (response) => {
        if (requestId !== this.currentRequestId) return;

        const items = response?.data?.items || [];
        this.totalRecords = response?.data?.totalRecord || 0;

        this.fullData = items.map((student: any) => {
          const mappedData = this.mapStudentData(student);
          mappedData.isDownloading = this.downloadingRowId === mappedData.id;
          return mappedData;
        });

        this.data = [...this.fullData];
        this.loading = false;
        this.isPageChangeLoading = false;
        this.currentPage = page;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (requestId !== this.currentRequestId) return;

        console.error('Failed to load students:', err);
        this.fullData = [];
        this.data = [];
        this.loading = false;
        this.isPageChangeLoading = false;
        this.showErrorToast(err?.message);
      },
    });
  }

  fetchTeacherStudents(page: number = 1): void {
    this.loading = true;
    const requestId = ++this.currentRequestId;

    let url: string;
    if (this.search && this.search.trim() !== '') {
      url = `teacherstudent/${this.schoolId}/students/get/search?search=${encodeURIComponent(this.search)}&page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    } else {
      url = `teacherstudent/${this.schoolId}/students?page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    }

    this.httpService.get<listAPIResponse>(url).subscribe({
      next: (response) => {
        if (requestId !== this.currentRequestId) return;
        const items = response?.data?.items || response?.data?.lists;
        this.totalRecords = response?.data?.totalRecord || 0;

        this.fullData = items.map((student: any) => {
          const mappedData = this.mapTeacherStudentData(student);
          mappedData.isDownloading = this.downloadingRowId === mappedData.id;
          return mappedData;
        });

        this.data = [...this.fullData];
        this.loading = false;
        this.isPageChangeLoading = false;
        this.currentPage = page;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (requestId !== this.currentRequestId) return;

        console.error('Failed to load teacher students:', err);
        this.fullData = [];
        this.data = [];
        this.loading = false;
        this.isPageChangeLoading = false;
        this.showErrorToast(err?.message);
      },
    });
  }

  onSort(event: {
    direction: 'asc' | 'desc' | '';
    column: string | null;
  }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.currentPage = 1;
    this.updateUrlWithPage(this.currentPage);
    if (this.teacherMode) {
      this.fetchTeacherStudents(this.currentPage);
    } else {
      this.fetchStudents(this.currentPage);
    }
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || this.isPageChangeLoading) return;

    this.isPageChangeLoading = true;
    this.currentPage = page;
    this.updateUrlWithPage(page);
    if (this.teacherMode) {
      this.fetchTeacherStudents(page);
    } else {
      this.fetchStudents(page);
    }
  }

  private updateUrlWithPage(page: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: page,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.updateUrlWithPage(1);
    if (this.teacherMode) {
      this.fetchTeacherStudents(1);
    } else {
      this.fetchStudents(1);
    }
  }

  hasFilterInput(): boolean {
    const { studentName, email, school, region, status } = this.userForm.value;
    return !!(
      studentName?.trim() ||
      email?.trim() ||
      school?.trim() ||
      region?.trim() ||
      status
    );
  }

  private updateRowDownloadingState(
    rowId: string,
    isDownloading: boolean,
  ): void {
    const fullDataIndex = this.fullData.findIndex((item) => item.id === rowId);
    if (fullDataIndex !== -1) {
      this.fullData[fullDataIndex].isDownloading = isDownloading;
    }
    const dataIndex = this.data.findIndex((item) => item.id === rowId);
    if (dataIndex !== -1) {
      this.data[dataIndex].isDownloading = isDownloading;
    }
  }

  onDownloadConsent(event: any, type: any): void {
    const studentID =
      event?.row?.id ||
      event?.row?.studentId ||
      event?.id ||
      event?.studentId;
    const consentType: 'account' | 'media' = type === 'media' ? 'media' : 'account';

    if (!studentID) {
      this.showErrorToast('Student id not found for consent download');
      return;
    }

    if (consentType === 'account') {
      this.isDownloadingAccount = true;
    } else {
      this.isDownloadingMedia = true;
    }

    this.downloadingRowId = studentID;
    this.downloadInProgress = true;
    this.updateRowDownloadingState(studentID, true);

    let url = '';
    if (consentType === 'account') {
      url = this.teacherMode
        ? `teacherstudent/${studentID}/parent-consent/pdf`
        : `students/${studentID}/parent-consent/pdf`;
    } else {
      url = this.teacherMode
        ? `teacherstudent/${studentID}/media-consent/pdf`
        : `students/${studentID}/media-consent/pdf`;
    }

    this.httpService.download(url).subscribe({
      next: (res) => {
        const blob = res.body;
        if (!blob) {
          this.showErrorToast('Empty PDF received');
          this.clearDownloadingState(studentID);
          this.resetDownloadingFlag(type);
          return;
        }

        const contentDisposition = res.headers.get('content-disposition') || '';
        const filename =
          this.extractFilename(contentDisposition) ||
          `${consentType === 'account' ? 'parent-consent' : 'media-consent'}-${studentID}.pdf`;

        this.triggerDownload(blob, filename);
        this.showSuccessToast(
          `${consentType === 'account' ? 'Parent' : 'Media'} consent downloaded successfully`,
        );
        this.clearDownloadingState(studentID);
        this.resetDownloadingFlag(consentType);
      },
      error: (err) => {
        this.showErrorToast(err?.message || 'Failed to download PDF');
        this.clearDownloadingState(studentID);
        this.resetDownloadingFlag(consentType);
      },
    });
  }

  private resetDownloadingFlag(type: 'account' | 'media'): void {
    if (type === 'account') {
      this.isDownloadingAccount = false;
    } else {
      this.isDownloadingMedia = false;
    }
  }

  private clearDownloadingState(rowId?: string): void {
    this.downloadInProgress = false;
    if (rowId) {
      this.updateRowDownloadingState(rowId, false);
    }
    this.downloadingRowId = null;
  }

  private extractFilename(cd: string): string | null {
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  onStatusUpdate(event: { row: any; id: any }): void {
    if (!this.userRoles.listItem.statusChange) return;
    const { row } = event;
    let newOnboarding = row.status == 'Complete on boarding';
    const currentStatus = row.status;
    const newStatus = currentStatus === 'Active' ? false : true;

    const dialogData: AlertDialogData = {
      title: 'Are you sure!',
      message: newOnboarding
        ? 'Are you sure you want to complete the student onboarding process?'
        : `Are you sure you want to ${currentStatus === 'Active' ? 'Deactivate' : 'Activate'} ?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      icon: 'warning',
      cancelButtonColor: '#EF4A00',
      okButtonColor: '#2CC3E8',
      submessage: '',
    };

    let payload;

    if (newOnboarding) {
      payload = {
        status: newStatus,
        onboarding: true,
      };
    } else {
      payload = {
        status: newStatus,
      };
    }
    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.httpService
            .put<ApiDataResponse>(
              this.teacherMode
                ? `teacherstudent/${row?.userID}/toggle-status`
                : `users/${row?.userId}/toggle-status`,
              payload,
            )
            .subscribe({
              next: (response) => {
                if (response.status) {
                  const newStatusValue = newStatus ? 'Active' : 'Inactive';
                  const fullDataIndex = this.fullData.findIndex(
                    (s) => s.id === row.id,
                  );
                  if (fullDataIndex !== -1) {
                    this.fullData[fullDataIndex] = {
                      ...this.fullData[fullDataIndex],
                      status: newStatusValue,
                    };
                  }
                  const dataIndex = this.data.findIndex((s) => s.id === row.id);
                  if (dataIndex !== -1) {
                    this.data[dataIndex] = {
                      ...this.data[dataIndex],
                      status: newStatusValue,
                    };
                  }

                  this.showSuccessToast(response.message);
                } else {
                  this.showErrorToast(response.message);
                }
              },
              error: (err) => this.showErrorToast(err?.message),
            });
        }
      });
  }

  ngAfterViewInit() {
    this.actionTemplateReady = true;
    this.cdr.detectChanges();
    setTimeout(() => this.resetTableScroll(), 0);
  }

  private resetTableScroll(): void {
    const tableContainer = document.querySelector('.table-container');
    if (tableContainer) {
      tableContainer.scrollLeft = 0;
    }
  }

  fetchViewStudents(id: string): void {
    this.loading = true;
    this.httpService.get<ApiDataResponse>(`students/${id}`).subscribe({
      next: (response) => {
        const data = response?.data;

        if (data && !Array.isArray(data)) {
          this.selectedStudent = {
            ...data,
            parentEmail: data.parentGuardianEmail ?? '',
            parentName: data.parentGuardianFullName ?? '',
          };
          this.parentConsent =
            data.parentApprovalStatus === 'Approved' ? true : false;
          this.checkCooldownFromConsentTime(data.mediaConsentSentAt);
        } else {
          this.selectedStudent = null;
          this.showErrorToast('Student data not found');
        }

        this.loading = false;
      },
      error: (err) => {
        this.selectedStudent = null;
        this.loading = false;
        this.handleApiError(
          err.message,
          'Something went wrong. Please try again later.',
        );
      },
    });
  }

  get filteredPast() {
    return (
      this.selectedStudent?.past?.filter(
        (past: { value: boolean }) => past.value === true,
      ) || []
    );
  }
  get filteredComp() {
    return (
      this.selectedStudent?.comp?.filter(
        (comp: { value: boolean }) => comp.value === true,
      ) || []
    );
  }

  getConsentStatusClass(status: string | null | undefined): string {
    const normalized = (status || '').trim().toLowerCase();
    if (normalized === 'approved') {
      return 'approved';
    }
    if (normalized === 'rejected' || normalized === 'declined') {
      return 'rejected';
    }
    return 'pending';
  }

  private mapEventViewStudentStatus(
    status: string | null | undefined,
  ): string | null {
    if (!status) {
      return null;
    }

    const trimmedStatus = status.trim();
    const normalizedStatus = trimmedStatus
      .toLowerCase()
      .replace(/[\s_-]+/g, '');

    const map: Record<string, string> = {
      newnononboarded: 'New Non Onboarded',
      pendingconsent: 'Pending Consent',
      preregistered: 'Pre Registered',
      registered: 'Registered',
      consentdeclined: 'Parent Declined',
      parentdeclined: 'Parent Declined',
    };

    return map[normalizedStatus] ?? trimmedStatus;
  }

  getProfileStudentStatusLabel(
    status: string | null | undefined,
  ): string | null {
    return this.mapEventViewStudentStatus(status);
  }

  getProfileStudentStatusColor(status: string | null | undefined): string {
    const label = this.getProfileStudentStatusLabel(status);
    const colorMap: Record<string, string> = {
      'Registered': '#4caf50',
      'Pre Registered': '#f5a623',
      'Pending Consent': '#f5a623',
      'New Non Onboarded': '#9e9e9e',
      'Parent Declined': '#f44336',
    };
    return colorMap[label ?? ''] ?? '#9e9e9e';
  }

  // Student view badge
  getStudentViewBadgeLabel(student: any): string | null {
    const parentApproval = (student?.parentApprovalStatus ?? '')
      .toString()
      .toLowerCase()
      .trim();

    // 1. Parent declined / rejected — check before isActive so it takes priority
    if (parentApproval === 'declined' || parentApproval === 'rejected')
      return 'Parent Declined';

    const rawStatus = (student?.status ?? '').toString().toLowerCase().trim();
    const isInactive =
      student?.isActive === false || rawStatus === 'inactive';

    // 2. Inactive
    if (isInactive) return 'Inactive';

    // 3. Not yet verified by teacher → Pending Verification
    //    Verified but parent hasn't responded yet → Pending Consent
    if (parentApproval === 'pending') {
      return student?.isTeacherVerified ? 'Pending Consent' : 'Pending Verification';
    }

    // 4. Parent approved — check if logged in at least once
    // lastLogin "0001-01-01T00:00:00" is C# default (no real login)
    if (parentApproval === 'approved') {
      const hasLoggedIn =
        student?.lastLogin &&
        new Date(student.lastLogin).getFullYear() > 1;
      return hasLoggedIn ? 'Registered' : 'Pre Registered';
    }

    // 5. No parent approval status yet — student just added, nothing done
    return 'New Non Onboarded';
  }

  getStudentViewBadgeColor(student: any): string {
    const label = this.getStudentViewBadgeLabel(student);
    const colorMap: Record<string, string> = {
      'Inactive': '#f5a623',
      'Registered': '#4caf50',
      'Pre Registered': '#f5a623',
      'Pending Verification': '#f5a623',
      'Pending Consent': '#f5a623',
      'New Non Onboarded': '#f5a623',
      'Parent Declined': '#f44336',
    };
    return colorMap[label ?? ''] ?? '#9e9e9e';
  }

  isConsentApproved(status: string | null | undefined): boolean {
    return (status || '').trim().toLowerCase() === 'approved';
  }

  canDownloadConsent(status: string | null | undefined): boolean {
    const normalized = (status || '').trim().toLowerCase();
    return normalized === 'approved';
  }

  shouldShowResendParentConsentButton(): boolean {
    if (!this.selectedStudent?.studentId) {
      return false;
    }

    if (!this.selectedStudent?.isTeacherVerified) {
      return false;
    }

    const parentStatus = (this.selectedStudent?.parentApprovalStatus || '')
      .trim()
      .toLowerCase();

    return !(
      parentStatus === 'approved' ||
      parentStatus === 'rejected' ||
      parentStatus === 'declined'
    );
  }

  verifyStudent(): void {
    const studentId = this.selectedStudent?.studentId;
    if (!studentId || this.isVerifyingStudent) return;

    this.isVerifyingStudent = true;
    this.httpService
      .post<ApiDataResponse>(`teacherstudent/${studentId}/verify`, {})
      .subscribe({
        next: (response) => {
          this.showSuccessToast(
            response?.message || 'Student verified. Parent consent request sent.',
          );
          this.fetchViewStudents(studentId);
          this.isVerifyingStudent = false;
        },
        error: (err) => {
          this.showErrorToast(
            err?.message || 'Failed to verify student.',
          );
          this.isVerifyingStudent = false;
        },
      });
  }

  resendParentConsent(): void {
    const studentId = this.selectedStudent?.studentId;
    if (!studentId || this.isResendingParentConsent) {
      return;
    }

    if (this.resendCooldownActive) {
      this.showResendCooldownError = true;
      return;
    }

    this.showResendCooldownError = false;
    this.isResendingParentConsent = true;
    const resendEndpoint = this.teacherMode
      ? `teacherstudent/${studentId}/parent-consent/resend`
      : `students/${studentId}/parent-consent/resend`;
    this.httpService
      .post<ApiDataResponse>(resendEndpoint, {})
      .subscribe({
        next: (response) => {
          this.showSuccessToast(
            response?.message || 'Reminder email sent successfully.',
          );
          this.fetchViewStudents(studentId);
          this.isResendingParentConsent = false;
          this.startResendCooldown();
        },
        error: (err) => {
          this.showErrorToast(
            err?.message || 'Failed to resend parent consent.',
          );
          this.isResendingParentConsent = false;
        },
      });
  }

  shouldShowResetParentConsentButton(): boolean {
    if (!this.selectedStudent?.studentId) return false;
    if (!this.userViewRoles?.resetConsent) return false;

    const parentStatus = (this.selectedStudent?.parentApprovalStatus || '')
      .trim()
      .toLowerCase();

    return parentStatus === 'approved' || parentStatus === 'declined' || parentStatus === 'rejected';
  }

  resetParentConsent(): void {
    const studentId = this.selectedStudent?.studentId;
    if (!studentId || this.isResettingParentConsent) return;

    const dialogData: AlertDialogData = {
      title: 'Reset Parent Consent',
      message: 'Are you sure you want to reset the parent consent decision?',
      submessage: 'This will set the status back to Pending, allowing the teacher to resend the consent request to the parent.',
      okText: 'Reset',
      cancelText: 'Cancel',
      okButtonColor: '#e74c3c',
      cancelButtonColor: 'var(--blue-color)',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.isResettingParentConsent = true;
        this.httpService
          .put<ApiDataResponse>(`students/${studentId}/parent-consent/reset`, {})
          .subscribe({
            next: (response) => {
              this.showSuccessToast(response?.message || 'Parent consent has been reset successfully.');
              this.fetchViewStudents(studentId);
              this.isResettingParentConsent = false;
            },
            error: (err) => {
              this.showErrorToast(err?.message || 'Failed to reset parent consent.');
              this.isResettingParentConsent = false;
            },
          });
      });
  }

  // AC2 / AC5: CSQ manual media consent toggle for students aged 16+
  onManualMediaConsentChange(approved: boolean): void {
    const studentId = this.selectedStudent?.studentId;
    if (!studentId || this.isUpdatingMediaConsent) return;

    const newStatus = approved ? 'Approved' : 'Declined';
    const dialogData: AlertDialogData = {
      title: 'Update Media Consent',
      message: `Are you sure you want to set Media Consent to ${newStatus}?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      okButtonColor: 'var(--blue-color)',
      cancelButtonColor: '#e74c3c',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.isUpdatingMediaConsent = true;
        this.httpService
          .put<ApiDataResponse>(`students/${studentId}/media-consent/manual-update`, { approved })
          .subscribe({
            next: (response) => {
              this.showSuccessToast(response?.message || `Media consent set to ${newStatus}.`);
              this.fetchViewStudents(studentId);
              this.isUpdatingMediaConsent = false;
            },
            error: (err) => {
              this.showErrorToast(err?.message || 'Failed to update media consent.');
              this.isUpdatingMediaConsent = false;
            },
          });
      });
  }

  private startResendCooldown(seconds: number = 10): void {
    if (seconds <= 0) return;
    this.resendCooldownActive = true;
    this.resendCooldownSeconds = seconds;
    if (this.resendCooldownInterval) {
      clearInterval(this.resendCooldownInterval);
    }
    this.resendCooldownInterval = setInterval(() => {
      this.resendCooldownSeconds--;
      if (this.resendCooldownSeconds <= 0) {
        clearInterval(this.resendCooldownInterval);
        this.resendCooldownInterval = null;
        this.resendCooldownActive = false;
        this.showResendCooldownError = false;
      }
    }, 1000);
  }

  private checkCooldownFromConsentTime(mediaConsentSentAt: string | null | undefined): void {
    if (!mediaConsentSentAt) return;
    const sentAt = new Date(this.asUtc(mediaConsentSentAt) as string);
    const elapsed = Math.floor((Date.now() - sentAt.getTime()) / 1000);
    const remaining = 10 - elapsed;
    if (remaining > 0) {
      this.startResendCooldown(remaining);
    }
  }

  asUtc(date: string | null | undefined): string | null {
    if (!date) return null;
    if (date.endsWith('Z') || date.endsWith('z') || /[+-]\d{2}:\d{2}$/.test(date)) return date;
    return date + 'Z';
  }

  fetchViewStudentsPoints(id: string): void {
    this.loading = true;
    this.httpService
      .get<ApiDataResponse>(`students/${id}/dashboard/points`)
      .subscribe({
        next: (response) => {
          if (response.data && !Array.isArray(response.data)) {
            this.pointsStudent = response.data;
          } else {
            this.pointsStudent = null;
            this.showErrorToast('Student data not found');
          }
          this.loading = false;
        },
        error: (err) => {
          this.pointsStudent = null;
          this.handleApiError(err.message, 'Failed to load student details');
        },
      });
  }

  fetchAssociatedTeacher(id: string): void {
    this.tabelLoading = true;
    this.httpService
      .get<ApiDataResponse>(`students/${id}/dashboard/associated-teachers`)
      .subscribe({
        next: (response) => {
          const data = response?.data;
          if (Array.isArray(data)) {
            this.associatedTeachersData = data.map((teacher) => ({
              id: teacher.teacherId ?? '',
              name: teacher.name ?? '',
              email: teacher.email ?? '',
              role: teacher.role ?? 'Teacher',
            }));
          } else {
            this.associatedTeachersData = [];
          }

          this.tabelLoading = false;
        },
        error: (err) => {
          this.tabelLoading = false;
          this.associatedTeachersData = [];
          this.handleApiError(err?.message, 'Failed to load student details');
        },
      });
  }

  fetchUpcomingEvents(id: string): void {
    this.tabelLoading = true;
    this.httpService
      .get<ApiDataResponse>(`students/${id}/dashboard/upcoming-events`)
      .subscribe({
        next: (response) => {
          const data = response?.data;
          if (Array.isArray(data)) {
            this.upcomingEventsData = data.map((events) => {
              const formatDate = (dateStr: {
                split: (
                  arg0: string,
                ) => { split: (arg0: string) => [any, any, any] }[];
              }) => {
                if (!dateStr) return '';
                const [year, month, day] = dateStr.split('T')[0].split('-');
                return `${day}/${month}/${year}`;
              };

              return {
                id: events.eventID ?? '',
                ['event name']: events.eventName ?? '',
                date: formatDate(events.eventStartDate) ?? '',
                status: events.status ?? '',
              };
            });
          } else {
            this.upcomingEventsData = [];
          }

          this.tabelLoading = false;
        },
        error: (err) => {
          this.tabelLoading = false;
          this.upcomingEventsData = [];
          this.handleApiError(err?.message, 'Failed to load student details');
        },
      });
  }

  fetchEventAttended(id: string): void {
    this.tabelLoading = true;
    this.httpService
      .get<ApiDataResponse>(`students/${id}/dashboard/events-attended`)
      .subscribe({
        next: (response) => {
          const data = response?.data;
          if (Array.isArray(data)) {
            this.eventAttendedsData = data.map((events) => {
              const formatDate = (dateStr: {
                split: (
                  arg0: string,
                ) => { split: (arg0: string) => [any, any, any] }[];
              }) => {
                if (!dateStr) return '';
                const [year, month, day] = dateStr.split('T')[0].split('-');
                return `${day}/${month}/${year}`;
              };

              return {
                id: events.eventID ?? '',
                ['event name']: events.eventName ?? '',
                date: formatDate(events.eventStartDate) ?? '',
                status: events.status ?? '',
              };
            });
          } else {
            this.eventAttendedsData = [];
          }

          this.tabelLoading = false;
        },
        error: (err) => {
          this.tabelLoading = false;
          this.eventAttendedsData = [];
          this.handleApiError(err?.message, 'Failed to load student details');
        },
      });
  }

  fetchStudentActivitiesForTeacher(id: string): void {
    this.teacherActivitiesLoading = true;

    const upcoming$ = this.httpService.get<ApiDataResponse>(
      `students/${id}/dashboard/upcoming-events`
    );
    const attended$ = this.httpService.get<ApiDataResponse>(
      `students/${id}/dashboard/events-attended`
    );

    forkJoin({ upcoming: upcoming$, attended: attended$ }).subscribe({
      next: ({ upcoming, attended }) => {
        const upcomingItems: any[] = Array.isArray(upcoming?.data) ? upcoming.data : [];
        const attendedItems: any[] = Array.isArray(attended?.data) ? attended.data : [];

        const mapItem = (item: any) => {
          const startDate = item?.eventStartDate ? new Date(item.eventStartDate) : null;
          const endDate = item?.eventEndDate ? new Date(item.eventEndDate) : null;
          const fmt = (d: Date | null): string =>
            d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
          const formattedStart = fmt(startDate);
          const formattedEnd = fmt(endDate);
          const formattedDate =
            formattedEnd && formattedStart !== formattedEnd
              ? `${formattedStart} - ${formattedEnd}`
              : formattedStart;
          const cb = (item?.createdBy ?? '').toLowerCase().replace(/\s+/g, '');
          const source =
            cb === 'csq' || cb === 'superadmin' || cb === 'csqadmin' || cb === 'csquser'
              ? 'CSQ'
              : cb === 'teacher'
              ? 'Teacher'
              : cb === 'student'
              ? 'Student'
              : '-';
          return {
            id: item?.eventId ?? item?.eventID ?? '',
            'event name': item?.eventName ?? '-',
            source,
            'Date & time': { date: formattedDate, time: item?.eventTime },
            location: item?.location ?? '-',
            status: item?.status ?? '-',
          };
        };

        // Merge: upcoming first, then attended — deduplicate by eventId
        const seen = new Set<string>();
        this.teacherActivitiesData = [...upcomingItems, ...attendedItems]
          .map(mapItem)
          .filter(row => {
            if (!row.id || seen.has(row.id)) return false;
            seen.add(row.id);
            return true;
          });

        this.teacherActivitiesLoading = false;
      },
      error: () => {
        this.teacherActivitiesData = [];
        this.teacherActivitiesLoading = false;
      },
    });
  }

  onViewActivityRow(row: any): void {
    const eventId = row?.id;
    if (!eventId) return;
    const source = (row?.source ?? '').toLowerCase();
    const eventCreator =
      source === 'teacher' || source === 'student' ? source : 'admin';
    const encodedId = this.urlParser.encodeId(eventId.toString());
    const encodedSegment = this.urlParser.encodeId(eventCreator);
    this.router.navigate(['/events/event-list/view', encodedId], {
      queryParams: { segment: encodedSegment },
    });
  }

  fetchSchoolsDropDown() {
    return this.httpService.get<ApiDataResponse>(`Schools/dropdown`).pipe(
      tap((response) => {
        if (Array.isArray(response.data)) {
          this.schoolOptions = response.data.map((item) => ({
            label: item.name,
            value: item.id,
          }));
        } else {
          this.schoolOptions = [];
          this.showErrorToast('Invalid school data format');
        }
      }),
    );
  }

  fetchTeachersDropDown(schoolId: string) {
    return this.httpService
      .get<ApiDataResponse>(`teachers/dropdown/${schoolId}`)
      .pipe(
        tap((response) => {
          if (Array.isArray(response.data) && response.data.length > 0) {
            this.teachersOption = response.data.map((item) => ({
              label: item.name,
              value: item.id,
            }));
            this.showSuccessToast(response?.message);
          } else {
            this.teachersOption = [];
            this.showErrorToast(response?.message || 'No teachers found');
          }
        }),
      );
  }

  // goToSchool(student: APIResponse | null): void {
  //   if (student?.schoolId) {
  //     this.selectedSchool = {};
  //     this.schoolTeacherData = [];
  //     this.schoolContactData = [];

  //     this.fetchStudentSchoolDetails(student.schoolId);
  //     this.fetchStudentSchoolTeachers(student.schoolId);
  //     this.fetchStudentSchoolContacts(student.schoolId);

  //     this.schoolDetailDialogRef = this.dialog.open(this.schoolDetailsDialog, {
  //       width: '72rem',
  //       maxWidth: '95vw',
  //       autoFocus: false,
  //       panelClass: 'custom-dialog-container',
  //     });

  //     this.schoolDetailDialogRef.afterClosed().subscribe(() => {
  //       this.selectedSchool = {};
  //       this.schoolTeacherData = [];
  //       this.schoolContactData = [];
  //       this.schoolDetailDialogRef = null;
  //     });
  //   }
  // }

  goToSchool(school: any): void {
    this.router.navigate(['/schools/view/' + school.schoolId]);
  }

  onStudentCellClick(event: { row: any; column: string }): void {
    if (event.column === 'student name') {
      this.onView(event.row);
    }
  }

  private fetchStudentSchoolDetails(id: any): void {
    this.schoolDialogLoading = true;
    this.httpService.get<ApiDataResponse>(`schools/${id}/details`).subscribe({
      next: (response) => {
        this.selectedSchool = response?.data ?? {};
      },
      error: () => {
        this.selectedSchool = {};
        this.showErrorToast('Failed to load school details');
      },
      complete: () => {
        this.schoolDialogLoading = false;
      },
    });
  }

  private fetchStudentSchoolTeachers(id: any): void {
    this.httpService.get<ApiDataResponse>(`schools/${id}/teachers`).subscribe({
      next: (response) => {
        if (Array.isArray(response?.data)) {
          this.schoolTeacherData = response.data.map((teacher) => ({
            id: teacher?.teacherId ?? '',
            'teacher name': [teacher?.firstName, teacher?.lastName]
              .filter(Boolean)
              .join(' '),
            email: teacher?.email ?? '',
            'phone/mobile': teacher?.phoneNumber ?? '',
          }));
        } else {
          this.schoolTeacherData = [];
        }
      },
      error: () => {
        this.schoolTeacherData = [];
      },
    });
  }

  private fetchStudentSchoolContacts(id: any): void {
    this.httpService.get<ApiDataResponse>(`schools/${id}/contacts`).subscribe({
      next: (response) => {
        if (Array.isArray(response?.data)) {
          this.schoolContactData = response.data.map((contact) => ({
            id: contact?.contactId ?? '',
            name: contact?.name ?? '',
            email: contact?.email ?? '',
            'phone number': contact?.phone ?? '',
            position: contact?.position ?? '',
          }));
        } else {
          this.schoolContactData = [];
        }
      },
      error: () => {
        this.schoolContactData = [];
      },
    });
  }

  openAddStudentDialog(
    teacherToEdit?: APIResponse,
    isEdit = false,
    width = '50.5rem',
  ): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.form.reset();

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: teacherToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    this.dialog
      .open(AddStudentComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        this.isProcessing = false;

        if (result) {
          if (this.viewMode && this.selectedStudentId) {
            this.fetchViewStudents(this.selectedStudentId);
          } else {
            this.fetchStudents(this.currentPage);
          }
        }
      });
  }

  openAddTeacherStudentDialog(
    teacherToEdit?: APIResponse,
    isEdit = false,
    width = '50.5rem',
  ): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.form.reset();

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: teacherToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    this.dialog
      .open(AddTeacherStudentComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (this.viewMode && this.selectedStudentId) {
            this.fetchViewStudents(this.selectedStudentId);
          } else {
            this.fetchTeacherStudents(this.currentPage);
          }
        }
        this.isProcessing = false;
      });
  }

  openAddBulkDialog(
    studentToEdit?: any,
    isEdit = false,
    width = '66.375rem',
  ): void {
    this.editingStudentIndex = null;

    if (isEdit && studentToEdit) {
      this.editingStudentIndex = this.data.findIndex(
        (s) => s.id === studentToEdit.id,
      );
      this.form.patchValue({
        firstName: studentToEdit.firstName || '',
        lastName: studentToEdit.lastName || '',
        personalEmail: studentToEdit.personalEmail || studentToEdit.email || '',
        school: studentToEdit.school || '',
      });
    } else {
      this.form.reset();
    }

    this.dialog
      .open(AddBulkDialogComponent, {
        data: {
          title: 'Bulk upload',
          confirmText: isEdit ? 'Update' : 'Upload',
          cancelText: 'Cancel',
          template: this.addStudentContent,
          type: this.teacherMode ? 'teacher' : 'student',
        },
        width,
        maxWidth: '100vw',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === true) {
          if (this.teacherMode) {
            this.fetchTeacherStudents(this.currentPage);
            this.showSuccessToast('Bulk upload completed successfully!');
          } else {
            this.fetchStudents(this.currentPage);
            this.showSuccessToast('Bulk upload completed successfully!');
          }
        }
        this.form.reset();
      });
  }

onView(student: any): void {
  this.selectedStudent = student;
  const encodedId = this.urlParser.encodeId(student.id.toString());
  this.router.navigate(['/students/view', encodedId]);
}
  goBack(): void {
    this.location.back();
  }

  onEdit(student: APIResponse | null): void {
    if (student) {
      if (this.teacherMode) {
        this.openAddTeacherStudentDialog(student, true);
      } else {
        this.openAddStudentDialog(student, true);
      }
    }
  }

  onDelete(student: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure want to delete ?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.httpService
            .delete<ApiDataResponse>(
              this.teacherMode
                ? `teacherstudent/${student?.id}`
                : `students/${student.id}`,
            )
            .subscribe({
              next: (response) => {
                if (response.status) {
                  this.fullData = this.fullData.filter(
                    (s) => s.id !== student.id,
                  );
                  this.data = this.data.filter((s) => s.id !== student.id);
                  this.adjustPaginationAfterDelete();
                  this.showSuccessToast(response.message);
                } else {
                  this.showErrorToast(response.message);
                }
              },
              error: (err) => this.showErrorToast(err?.message),
            });
        }
      });
  }

  private adjustPaginationAfterDelete(): void {
    const totalPages = Math.ceil(this.totalRecords / this.pageSize);
    if (this.currentPage > totalPages && totalPages > 0) {
      this.currentPage = totalPages;
      this.updateUrlWithPage(this.currentPage);
      if (this.teacherMode) {
        this.fetchTeacherStudents(this.currentPage);
      } else {
        this.fetchStudents(this.currentPage);
      }
    } else {
      if (this.teacherMode) {
        this.fetchTeacherStudents(this.currentPage);
      } else {
        this.fetchStudents(this.currentPage);
      }
    }
  }

  switchSegment(
    segment:
      | 'recentlyRegisteredEvents'
      | 'eventattended'
      | 'associatedTeachers'
      | 'notes',
  ): void {
    this.selectedSegment = segment;
    if (segment === 'associatedTeachers' && this.selectedStudentId) {
      this.fetchAssociatedTeacher(this.selectedStudentId);
    } else if (
      segment === 'recentlyRegisteredEvents' &&
      this.selectedStudentId
    ) {
      this.fetchUpcomingEvents(this.selectedStudentId);
    } else if (segment === 'eventattended' && this.selectedStudentId) {
      this.fetchEventAttended(this.selectedStudentId);
    }
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  get selectedColumns() {
    if (this.teacherMode) {
      return this.columnsTeacher;
    }
    const role = localStorage.getItem('userRole') ?? '';
    if (role === 'csquser' || role === 'csqadmin' || role === 'superadmin') {
      return this.columnsCSQUser;
    }
    return this.columns;
  }

  transformApiData(items: APIResponse[]): Record<string, string | number>[] {
    return items.map((user) => ({
      'Student name': `${user.firstName} ${user.lastName}`,
      Email: user.schoolEmail ?? '-',
      'School name': user.schoolName ?? '-',
      Age: user?.age ?? '-',
      'Parent/Guardian full name': user?.parentGuardianFullName
        ? user?.parentGuardianFullName
        : '-',
      'Parent/Guardian email': user?.parentGuardianEmail
        ? user?.parentGuardianEmail
        : '-',
      'PD points': user.pdPoints ?? 0,
      'Last login / Last updated': this.formatStudentLastAccess(user),
      'Date registered': user.dateRegistered
        ? new Date(user.dateRegistered).toLocaleDateString('en-GB')
        : '',
      'Registration type': user.registrationType ?? '',
      'Parent consent':
        user.parentApprovalStatus === 'Approved' ||
        user.parentApprovalStatus === 'Declined' ||
        user.parentApprovalStatus === 'Rejected'
          ? 'Download'
          : 'Pending',
      Status: user.status ?? 'Active',
      status:
        user.status == 'Pending' &&
        !user.lastLogin &&
        user.registrationType == 'Teacher Registered'
          ? 'Complete on boarding'
          : (user.status ?? 'Active'),
    }));
  }

  formatStudentLastAccess(student: APIResponse | any): string {
    const reference = student?.lastLogin || student?.lastUpdated;
    return reference ? new Date(reference).toLocaleDateString('en-GB') : '';
  }

  formatSchoolAddress(school: any): string {
    return [school?.address, school?.suburb, school?.state, school?.postcode]
      .filter(Boolean)
      .join(', ');
  }

  formatSchoolContactSummary(contact: any): string {
    return [
      contact?.name,
      contact?.email,
      contact?.['phone number'],
      contact?.position,
    ]
      .filter(Boolean)
      .join(' | ');
  }
}
